
import { useState, useRef } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import toast, { Toaster } from "react-hot-toast";
import { AnimatedText } from "../ui/animated-text";
import { GlowyCard } from "../ui/glowy-card";
import { ShinyButton } from "../ui/shiny-button";
import { HugeiconsIcon } from "@hugeicons/react";
import { ArrowLeft01Icon } from "@hugeicons/core-free-icons";
import { cn } from "@/lib/utils";

interface InstallationPageProps {
    onClose: () => void;
}

export function InstallationPage({ onClose }: InstallationPageProps) {
    const [form, setForm] = useState({
        name: "",
        email: "",
        phone: "",
        company: "",
        address: "",
        city: "",
        postalCode: "",
        numberOfUnits: "",
        message: "",
        cat: "", // Honeypot field
    });

    const [errors, setErrors] = useState<Record<string, string>>({});
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isSubmitted, setIsSubmitted] = useState(false);
    const confirmationRef = useRef<HTMLDivElement>(null);

    const validateEmail = (email: string): boolean => {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return emailRegex.test(email);
    };

    const isFormValid = (): boolean => {
        return (
            form.name.trim() !== "" &&
            form.email.trim() !== "" &&
            validateEmail(form.email) &&
            form.phone.trim() !== "" &&
            form.company.trim() !== "" &&
            form.address.trim() !== "" &&
            form.city.trim() !== "" &&
            form.postalCode.trim() !== "" &&
            form.numberOfUnits.trim() !== ""
        );
    };

    const handleChange = (field: string, value: string) => {
        setForm((prev) => ({ ...prev, [field]: value }));

        // Clear error when user starts typing
        if (errors[field]) {
            setErrors((prev) => {
                const newErrors = { ...prev };
                delete newErrors[field];
                return newErrors;
            });
        }

        // Real-time validation
        if (field === "email" && value.trim() !== "") {
            if (!validateEmail(value)) {
                setErrors((prev) => ({ ...prev, email: "Bitte geben Sie eine gültige E-Mail-Adresse ein" }));
            }
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        // Check honeypot - silent reject if filled
        if (form.cat.length > 0) {
            return;
        }

        // Validate required fields
        if (!form.name.trim()) {
            toast.error("Bitte geben Sie Ihren Namen ein");
            setErrors((prev) => ({ ...prev, name: "Dieses Feld ist erforderlich" }));
            return;
        }

        if (!validateEmail(form.email)) {
            toast.error("Bitte geben Sie eine gültige E-Mail-Adresse ein");
            setErrors((prev) => ({ ...prev, email: "Bitte geben Sie eine gültige E-Mail-Adresse ein" }));
            return;
        }

        if (!form.phone.trim()) {
            toast.error("Bitte geben Sie Ihre Telefonnummer ein");
            setErrors((prev) => ({ ...prev, phone: "Dieses Feld ist erforderlich" }));
            return;
        }

        if (!form.address.trim()) {
            toast.error("Bitte geben Sie Ihre Adresse ein");
            setErrors((prev) => ({ ...prev, address: "Dieses Feld ist erforderlich" }));
            return;
        }

        if (!form.city.trim()) {
            toast.error("Bitte geben Sie Ihre Stadt ein");
            setErrors((prev) => ({ ...prev, city: "Dieses Feld ist erforderlich" }));
            return;
        }

        if (!form.postalCode.trim()) {
            toast.error("Bitte geben Sie Ihre Postleitzahl ein");
            setErrors((prev) => ({ ...prev, postalCode: "Dieses Feld ist erforderlich" }));
            return;
        }

        if (!form.company.trim()) {
            toast.error("Bitte geben Sie Ihren Unternehmensnamen ein");
            setErrors((prev) => ({ ...prev, company: "Dieses Feld ist erforderlich" }));
            return;
        }

        if (!form.numberOfUnits.trim()) {
            toast.error("Bitte geben Sie die Anzahl der benötigten TBOXen ein");
            setErrors((prev) => ({ ...prev, numberOfUnits: "Dieses Feld ist erforderlich" }));
            return;
        }

        setIsSubmitting(true);

        try {
            // Build message with all Einbauer registration details
            let messageParts: string[] = [];

            messageParts.push("Einbauer Registrierung");
            messageParts.push("");

            messageParts.push("Kontaktdaten:");
            messageParts.push(`Name: ${form.name.trim()}`);
            messageParts.push(`E-Mail: ${form.email.trim()}`);
            messageParts.push(`Unternehmen: ${form.company.trim()}`);
            messageParts.push("");

            messageParts.push("Einbauer Standort:");
            messageParts.push(`${form.address.trim()}`);
            messageParts.push(`${form.postalCode.trim()} ${form.city.trim()}`);
            messageParts.push("");

            messageParts.push("Bestelldetails:");
            messageParts.push(`Anzahl benötigter TBOXen: ${form.numberOfUnits.trim()}`);
            messageParts.push("");

            if (form.message.trim()) {
                messageParts.push("Zusätzliche Informationen:");
                messageParts.push(form.message.trim());
            }

            // Prepare form data matching ContactForm structure
            const formData: { name: string; email: string; phone?: string; message: string } = {
                name: form.name.trim(),
                email: form.email.trim(),
                message: messageParts.join("\n"),
            };

            // Append phone to message if provided (matching ContactForm pattern)
            if (form.phone.trim()) {
                formData.message = `${formData.message}\n\nTelefon: ${form.phone.trim()}`;
            }

            // Get API base URL from environment variable
            let apiBaseUrl = "https://api.app.taxifusion.de";
            try {
                if (typeof import.meta !== 'undefined' && import.meta.env && import.meta.env.VITE_API_BASE_URL) {
                    apiBaseUrl = import.meta.env.VITE_API_BASE_URL;
                }
            } catch (e) {
                console.warn("Could not access environment variable, using default API URL");
            }

            // POST to API
            await axios.post(`${apiBaseUrl}/contact`, formData);

            // Success
            setIsSubmitted(true);
            toast.success("Registrierung erfolgreich gesendet! Wir melden uns bald bei Ihnen.");

            // Scroll to confirmation
            setTimeout(() => {
                confirmationRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
            }, 100);

            // Reset form
            setForm({
                name: "",
                email: "",
                phone: "",
                company: "",
                address: "",
                city: "",
                postalCode: "",
                numberOfUnits: "",
                message: "",
                cat: "",
            });
        } catch (error) {
            console.error("Form submission error:", error);
            toast.error(
                "Fehler beim Senden der Anfrage. Bitte versuchen Sie es später erneut oder kontaktieren Sie uns direkt."
            );
        } finally {
            setIsSubmitting(false);
        }
    };

    const isValid = isFormValid();

    return (
        <div className="min-h-screen bg-black text-white selection:bg-white/20 overflow-x-hidden">
            <div className="container mx-auto max-w-4xl px-4 sm:px-6 py-12 sm:py-16">
                {/* Header */}
                <div className="mb-8 sm:mb-12">
                    <button
                        onClick={onClose}
                        className="flex items-center gap-2 text-gray-400 hover:text-white transition-colors mb-6 sm:mb-8"
                    >
                        <HugeiconsIcon icon={ArrowLeft01Icon} size={20} />
                        <span>Zurück zur Startseite</span>
                    </button>

                    <AnimatedText
                        lines={["Als Einbauer registrieren"]}
                        className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold mb-4 sm:mb-6 tracking-tighter"
                        direction="up"
                        as="h1"
                    />
                    <p className="text-base sm:text-lg md:text-xl text-gray-400 max-w-2xl">
                        Sie möchten TBOX in Fahrzeuge Ihrer Kunden einbauen? Registrieren Sie sich als Einbauer.
                        Füllen Sie das Formular aus, um TBOXen zu bestellen.
                    </p>
                </div>

                <GlowyCard>
                    {!isSubmitted ? (
                        <form onSubmit={handleSubmit} className="space-y-6">
                            {/* Honeypot field - hidden from users */}
                            <input
                                type="text"
                                name="cat"
                                value={form.cat}
                                onChange={(e) => handleChange("cat", e.target.value)}
                                className="absolute opacity-0 pointer-events-none h-0 w-0"
                                tabIndex={-1}
                                autoComplete="off"
                                aria-hidden="true"
                            />

                            {/* Contact Information Section */}
                            <div>
                                <h3 className="text-lg font-bold text-white mb-4">Kontaktdaten</h3>
                                <div className="space-y-4">
                                    <div>
                                        <label htmlFor="name" className="block text-sm font-medium text-gray-300 mb-2">
                                            Name <span className="text-red-400">*</span>
                                        </label>
                                        <input
                                            type="text"
                                            id="name"
                                            required
                                            value={form.name}
                                            onChange={(e) => handleChange("name", e.target.value)}
                                            className={cn(
                                                "w-full px-4 py-3 rounded-xl border bg-white/5 text-white placeholder-gray-500",
                                                "focus:outline-none focus:ring-2 focus:ring-white/20 focus:border-white/20",
                                                "transition-all duration-200",
                                                errors.name ? "border-red-500/50" : "border-white/10"
                                            )}
                                            placeholder="Ihr vollständiger Name"
                                        />
                                        {errors.name && (
                                            <p className="mt-1 text-sm text-red-400">{errors.name}</p>
                                        )}
                                    </div>

                                    <div className="grid sm:grid-cols-2 gap-4">
                                        <div>
                                            <label htmlFor="email" className="block text-sm font-medium text-gray-300 mb-2">
                                                E-Mail <span className="text-red-400">*</span>
                                            </label>
                                            <input
                                                type="email"
                                                id="email"
                                                required
                                                value={form.email}
                                                onChange={(e) => handleChange("email", e.target.value)}
                                                className={cn(
                                                    "w-full px-4 py-3 rounded-xl border bg-white/5 text-white placeholder-gray-500",
                                                    "focus:outline-none focus:ring-2 focus:ring-white/20 focus:border-white/20",
                                                    "transition-all duration-200",
                                                    errors.email ? "border-red-500/50" : "border-white/10"
                                                )}
                                                placeholder="ihre.email@beispiel.de"
                                            />
                                            {errors.email && (
                                                <p className="mt-1 text-sm text-red-400">{errors.email}</p>
                                            )}
                                        </div>

                                        <div>
                                            <label htmlFor="phone" className="block text-sm font-medium text-gray-300 mb-2">
                                                Telefon <span className="text-red-400">*</span>
                                            </label>
                                            <input
                                                type="tel"
                                                id="phone"
                                                required
                                                value={form.phone}
                                                onChange={(e) => handleChange("phone", e.target.value)}
                                                className={cn(
                                                    "w-full px-4 py-3 rounded-xl border bg-white/5 text-white placeholder-gray-500",
                                                    "focus:outline-none focus:ring-2 focus:ring-white/20 focus:border-white/20",
                                                    "transition-all duration-200",
                                                    errors.phone ? "border-red-500/50" : "border-white/10"
                                                )}
                                                placeholder="030 12345678"
                                            />
                                            {errors.phone && (
                                                <p className="mt-1 text-sm text-red-400">{errors.phone}</p>
                                            )}
                                        </div>
                                    </div>

                                    <div>
                                        <label htmlFor="company" className="block text-sm font-medium text-gray-300 mb-2">
                                            Unternehmen <span className="text-red-400">*</span>
                                        </label>
                                        <input
                                            type="text"
                                            id="company"
                                            required
                                            value={form.company}
                                            onChange={(e) => handleChange("company", e.target.value)}
                                            className={cn(
                                                "w-full px-4 py-3 rounded-xl border bg-white/5 text-white placeholder-gray-500",
                                                "focus:outline-none focus:ring-2 focus:ring-white/20 focus:border-white/20",
                                                "transition-all duration-200",
                                                errors.company ? "border-red-500/50" : "border-white/10"
                                            )}
                                            placeholder="Ihr Unternehmen"
                                        />
                                        {errors.company && (
                                            <p className="mt-1 text-sm text-red-400">{errors.company}</p>
                                        )}
                                    </div>
                                </div>
                            </div>

                            {/* Einbauer Location Section */}
                            <div>
                                <h3 className="text-lg font-bold text-white mb-4">Ihr Standort</h3>
                                <div className="space-y-4">
                                    <div>
                                        <label htmlFor="address" className="block text-sm font-medium text-gray-300 mb-2">
                                            Straße und Hausnummer <span className="text-red-400">*</span>
                                        </label>
                                        <input
                                            type="text"
                                            id="address"
                                            required
                                            value={form.address}
                                            onChange={(e) => handleChange("address", e.target.value)}
                                            className={cn(
                                                "w-full px-4 py-3 rounded-xl border bg-white/5 text-white placeholder-gray-500",
                                                "focus:outline-none focus:ring-2 focus:ring-white/20 focus:border-white/20",
                                                "transition-all duration-200",
                                                errors.address ? "border-red-500/50" : "border-white/10"
                                            )}
                                            placeholder="Musterstraße 123"
                                        />
                                        {errors.address && (
                                            <p className="mt-1 text-sm text-red-400">{errors.address}</p>
                                        )}
                                    </div>

                                    <div className="grid sm:grid-cols-3 gap-4">
                                        <div className="sm:col-span-2">
                                            <label htmlFor="city" className="block text-sm font-medium text-gray-300 mb-2">
                                                Stadt <span className="text-red-400">*</span>
                                            </label>
                                            <input
                                                type="text"
                                                id="city"
                                                required
                                                value={form.city}
                                                onChange={(e) => handleChange("city", e.target.value)}
                                                className={cn(
                                                    "w-full px-4 py-3 rounded-xl border bg-white/5 text-white placeholder-gray-500",
                                                    "focus:outline-none focus:ring-2 focus:ring-white/20 focus:border-white/20",
                                                    "transition-all duration-200",
                                                    errors.city ? "border-red-500/50" : "border-white/10"
                                                )}
                                                placeholder="Berlin"
                                            />
                                            {errors.city && (
                                                <p className="mt-1 text-sm text-red-400">{errors.city}</p>
                                            )}
                                        </div>

                                        <div>
                                            <label htmlFor="postalCode" className="block text-sm font-medium text-gray-300 mb-2">
                                                PLZ <span className="text-red-400">*</span>
                                            </label>
                                            <input
                                                type="text"
                                                id="postalCode"
                                                required
                                                value={form.postalCode}
                                                onChange={(e) => handleChange("postalCode", e.target.value)}
                                                className={cn(
                                                    "w-full px-4 py-3 rounded-xl border bg-white/5 text-white placeholder-gray-500",
                                                    "focus:outline-none focus:ring-2 focus:ring-white/20 focus:border-white/20",
                                                    "transition-all duration-200",
                                                    errors.postalCode ? "border-red-500/50" : "border-white/10"
                                                )}
                                                placeholder="10115"
                                            />
                                            {errors.postalCode && (
                                                <p className="mt-1 text-sm text-red-400">{errors.postalCode}</p>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Order Details Section */}
                            <div>
                                <h3 className="text-lg font-bold text-white mb-4">Bestelldetails</h3>
                                <div className="space-y-4">
                                    <div>
                                        <label htmlFor="numberOfUnits" className="block text-sm font-medium text-gray-300 mb-2">
                                            Anzahl benötigter TBOXen <span className="text-red-400">*</span>
                                        </label>
                                        <input
                                            type="number"
                                            id="numberOfUnits"
                                            required
                                            min="1"
                                            value={form.numberOfUnits}
                                            onChange={(e) => handleChange("numberOfUnits", e.target.value)}
                                            className={cn(
                                                "w-full px-4 py-3 rounded-xl border bg-white/5 text-white placeholder-gray-500",
                                                "focus:outline-none focus:ring-2 focus:ring-white/20 focus:border-white/20",
                                                "transition-all duration-200",
                                                errors.numberOfUnits ? "border-red-500/50" : "border-white/10"
                                            )}
                                            placeholder="z.B. 5"
                                        />
                                        {errors.numberOfUnits && (
                                            <p className="mt-1 text-sm text-red-400">{errors.numberOfUnits}</p>
                                        )}
                                    </div>

                                    <div>
                                        <label htmlFor="message" className="block text-sm font-medium text-gray-300 mb-2">
                                            Zusätzliche Informationen <span className="text-gray-500 text-xs">(optional)</span>
                                        </label>
                                        <textarea
                                            id="message"
                                            rows={4}
                                            value={form.message}
                                            onChange={(e) => handleChange("message", e.target.value)}
                                            className={cn(
                                                "w-full px-4 py-3 rounded-xl border bg-white/5 text-white placeholder-gray-500",
                                                "focus:outline-none focus:ring-2 focus:ring-white/20 focus:border-white/20",
                                                "transition-all duration-200 resize-none border-white/10"
                                            )}
                                            placeholder="Weitere Informationen zur Bestellung..."
                                        />
                                    </div>
                                </div>
                            </div>

                            {/* Submit button */}
                            <div className="pt-4">
                                <ShinyButton
                                    type="submit"
                                    glow={isValid}
                                    disabled={!isValid || isSubmitting}
                                    className={cn(
                                        "w-full sm:w-auto min-h-[44px]",
                                        (!isValid || isSubmitting) && "opacity-50 cursor-not-allowed"
                                    )}
                                >
                                    {isSubmitting ? "Wird gesendet..." : "Registrierung absenden"}
                                </ShinyButton>
                            </div>
                        </form>
                    ) : (
                        <motion.div
                            ref={confirmationRef}
                            initial={{ opacity: 0, scale: 0.9 }}
                            animate={{ opacity: 1, scale: 1 }}
                            transition={{ duration: 0.5 }}
                            className="text-center py-8"
                        >
                            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-green-500/20 border border-green-500/50 mb-6">
                                <svg
                                    className="w-8 h-8 text-green-400"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                >
                                    <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M5 13l4 4L19 7"
                                    />
                                </svg>
                            </div>
                            <h3 className="text-2xl font-bold text-white mb-2">Registrierung gesendet!</h3>
                            <p className="text-gray-400 mb-6">
                                Vielen Dank für Ihre Registrierung als Einbauer. Wir melden uns in Kürze bei Ihnen,
                                um die Bestelldetails zu besprechen.
                            </p>
                            <div className="flex flex-col sm:flex-row gap-3 justify-center">
                                <ShinyButton onClick={onClose} className="min-h-[44px]">
                                    Zurück zur Startseite
                                </ShinyButton>
                                <button
                                    onClick={() => setIsSubmitted(false)}
                                    className="px-6 py-3 text-sm text-gray-400 hover:text-white transition-colors underline"
                                >
                                    Weitere Anfrage senden
                                </button>
                            </div>
                        </motion.div>
                    )}
                </GlowyCard>
            </div>

            {/* Toast notifications */}
            <Toaster
                position="top-center"
                toastOptions={{
                    duration: 4000,
                    style: {
                        background: "#1a1a1a",
                        color: "#fff",
                        border: "1px solid rgba(255, 255, 255, 0.1)",
                        borderRadius: "12px",
                    },
                    success: {
                        iconTheme: {
                            primary: "#10b981",
                            secondary: "#fff",
                        },
                    },
                    error: {
                        iconTheme: {
                            primary: "#ef4444",
                            secondary: "#fff",
                        },
                    },
                }}
            />
        </div>
    );
}
